import { receivedRequestProvider } from '@core/domain/product'

export default {
    props: {
        showStepNumberPrefix: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            flowSteps: [],
            validSteps: [],
            doneClickedSteps: [],
            completedTransientFlowStepIds: [],
        }
    },
    computed: {
        stepIds() {
            return this.flowSteps.reduce((stepIds, step) => {
                if (!step.key) {
                    return stepIds
                }

                return {
                    ...stepIds,
                    [step.key]: step.id,
                }
            }, {})
        },
        visibleFlowSteps() {
            return this.flowSteps.filter(step => this.isFlowStepVisible(step))
        },
        enabledFlowSteps() {
            return this.visibleFlowSteps.filter(step => !this.isStepDisabled(step.id))
        },
        doneSteps() {
            return this.request.doneSteps ? this.request.doneSteps : []
        },
        doneStepIds() {
            return this.doneSteps.map(step => this.normalizeStepId(step))
        },
    },
    methods: {
        changeStepValidity(step, isValid) {
            const stepId = this.normalizeStepId(step)
            if (isValid && !this.validSteps.includes(stepId)) {
                this.validSteps.push(stepId)
            } else if (!isValid && this.validSteps.includes(stepId)) {
                this.validSteps.splice(this.validSteps.findIndex(s => s === stepId), 1)
            }
        },
        gotoNextStep(step) {
            const stepId = this.normalizeStepId(step)
            const stepConfig = this.getStepConfig(stepId)

            if (stepConfig && stepConfig.replyFallback) {
                if (this.isApproveDeclarationStepVisible && !this.isApproveDeclarationStepDisabled) {
                    this.updateStepVisible(stepId)
                } else if (this.$refs.stepSendReply) {
                    this.$refs.stepSendReply.updateVisible()
                }
                return
            }

            this.updateStepVisible(stepId)
        },
        doneStep(step) {
            const stepId = this.normalizeStepId(step)
            if (!this.doneClickedSteps.includes(stepId)) {
                this.doneClickedSteps.push(stepId)
            }

            const nextStepId = this.getNextStepId(stepId)
            const isVisible = this.isStepVisible(nextStepId)
            if (this.shouldMarkStepValidOnDone(stepId)) {
                this.changeStepValidity(stepId, true)
            }
            this.markStepAsDone(stepId)

            if (isVisible) {
                this.gotoNextStep(nextStepId)
                setTimeout(() => {
                    this.smoothScroll(nextStepId)
                }, 200)
                return
            }

            setTimeout(() => {
                this.gotoNextStep(nextStepId)
                setTimeout(() => {
                    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
                }, 600)
            }, 100)
        },
        smoothScroll(step) {
            const stepConfig = this.getStepConfig(step)
            if (!stepConfig || !this.$refs[stepConfig.ref]) {
                return
            }

            this.$refs[stepConfig.ref].$el.scrollIntoView({
                behavior: 'smooth',
            })
        },
        isStepValid(step) {
            return this.validSteps.includes(this.normalizeStepId(step))
        },
        isStepVisible(step) {
            const stepId = this.normalizeStepId(step)
            const stepIndex = this.visibleFlowSteps.findIndex(s => s.id === stepId)
            if (stepIndex <= 0) {
                return false
            }

            const previousStep = this.getPreviousEnabledStep(stepId)
            if (!previousStep) {
                return false
            }

            return this.isStepDone(previousStep.id) || this.isStepDone(stepId)
        },
        getStepVariantStyles(step) {
            return this.isStepValid(step) && this.isStepDone(step)
                ? { bg: 'default', text: 'default', class: 'done' }
                : { bg: 'default', text: 'default', class: '' }
        },
        normalizeStepId(step) {
            if (typeof step === 'number') {
                const stepConfig = this.flowSteps.find(s => s.persistedStep === step)
                return stepConfig ? stepConfig.id : step
            }

            return step
        },
        toPersistedStep(step) {
            const stepConfig = this.getStepConfig(step)
            return stepConfig ? stepConfig.persistedStep : step
        },
        getStepConfig(step) {
            const stepId = this.normalizeStepId(step)
            return this.flowSteps.find(s => s.id === stepId)
        },
        getNextStepId(step) {
            const stepId = this.normalizeStepId(step)
            const stepIndex = this.enabledFlowSteps.findIndex(s => s.id === stepId)
            const nextStep = this.enabledFlowSteps[stepIndex + 1]
            return nextStep ? nextStep.id : null
        },
        getPreviousEnabledStep(step) {
            const stepId = this.normalizeStepId(step)
            const visibleStepIndex = this.visibleFlowSteps.findIndex(s => s.id === stepId)
            if (visibleStepIndex <= 0) {
                return null
            }

            return this.visibleFlowSteps
                .slice(0, visibleStepIndex)
                .reverse()
                .find(item => !this.isStepDisabled(item.id)) || null
        },
        updateStepVisible(step) {
            const stepConfig = this.getStepConfig(step)
            if (stepConfig && this.$refs[stepConfig.ref]) {
                this.$refs[stepConfig.ref].updateVisible()
            } else if (!stepConfig && this.$refs.stepSendReply) {
                this.$refs.stepSendReply.updateVisible()
            }
        },
        isStepDone(step) {
            const stepId = this.normalizeStepId(step)
            return this.doneStepIds.includes(stepId) || this.completedTransientFlowStepIds.includes(stepId)
        },
        markStepAsDone(step) {
            const request = receivedRequestProvider(this.id)
            const stepConfig = this.getStepConfig(step)
            const persistedStep = this.toPersistedStep(step)

            if (stepConfig && stepConfig.persistProgress === false) {
                if (!this.completedTransientFlowStepIds.includes(stepConfig.id)) {
                    this.completedTransientFlowStepIds.push(stepConfig.id)
                }
                return
            }

            if (!request.isStepDone(persistedStep)) {
                request.markStepAsDone(persistedStep)
                request.save()
            }
        },
        markStepAsUnDone(step) {
            const request = receivedRequestProvider(this.id)
            request.markStepAsUnDone(this.toPersistedStep(step))
            return request.save()
        },
        getStepTitle(step) {
            const stepConfig = this.getStepConfig(step)
            if (!stepConfig) {
                return ''
            }

            const title = this.getStepTitleText(stepConfig)
            if (!this.showStepNumberPrefix || stepConfig.numbered === false) {
                return title
            }

            const displayNumber = this.getStepDisplayNumber(stepConfig)
            const titleWithNumberKey = 'Component(Step):title-with-number'
            const titleWithNumber = this.$t(titleWithNumberKey, {
                number: displayNumber,
                title,
            })

            if (titleWithNumber !== titleWithNumberKey) {
                return titleWithNumber
            }

            const prefixKey = 'Component(Step):number-prefix'
            const prefix = this.$t(prefixKey, { number: displayNumber })
            const resolvedPrefix = prefix === prefixKey ? `Step ${displayNumber}` : prefix

            return `${resolvedPrefix} - ${title}`
        },
        getStepDisplayNumber(stepConfig) {
            const index = this.visibleFlowSteps.findIndex(step => step.id === stepConfig.id)
            return index >= 0 ? index + 1 : stepConfig.persistedStep
        },
        getStepTitleText(stepConfig) {
            if (!stepConfig.titleKey) {
                return stepConfig.title || ''
            }

            const title = this.$t(stepConfig.titleKey, stepConfig.titleParams || {})
            return title === stepConfig.titleKey ? stepConfig.title : title
        },
        declarationApproved() {
            const stepConfig = this.flowSteps.find(step => step.approvalStep)
            if (!stepConfig) {
                return
            }

            this.isDeclarationApproved = true
            this.changeStepValidity(stepConfig.id, true)
            if (this.isBulkReplyCandidatesStepVisible) {
                this.$nextTick(() => {
                    if (this.$refs.stepBulkReplyCandidates) {
                        this.$refs.stepBulkReplyCandidates.updateVisible(true)
                    } else if (this.$refs.stepSendReply) {
                        this.$refs.stepSendReply.updateVisible(true)
                    }
                })
            } else if (this.$refs.stepSendReply) {
                this.$nextTick(() => {
                    this.$refs.stepSendReply.updateVisible(true)
                })
            }

            this.$store.dispatch('declarationApproved')
        },
        isFlowStepVisible(step) {
            if (!step) {
                return false
            }

            if (typeof step.isVisible === 'function') {
                return step.isVisible(this)
            }

            if (typeof step.isVisible === 'string') {
                return Boolean(this[step.isVisible])
            }

            return step.isVisible !== false
        },
        isStepDisabled(step) {
            const stepConfig = this.getStepConfig(step)
            if (!stepConfig) {
                return false
            }

            if (typeof stepConfig.isDisabled === 'function') {
                return stepConfig.isDisabled(this)
            }

            if (typeof stepConfig.isDisabled === 'string') {
                return Boolean(this[stepConfig.isDisabled])
            }

            return stepConfig.isDisabled === true
        },
        shouldMarkStepValidOnDone(step) {
            const stepConfig = this.getStepConfig(step)
            if (!stepConfig) {
                return false
            }

            return (stepConfig.validityMode || 'manual') === 'done-click'
        },
    },
}
